import { yupResolver } from "@hookform/resolvers/yup";
import { XCircle } from "phosphor-react";
import { Controller, useForm } from "react-hook-form";
import { useIntl } from "react-intl";
import { toast } from "react-toastify";

import { useNavigate } from "react-router-dom";
import { Button } from "../../../../../components/Button";
import { CountrySelector } from "../../../../../components/CountrySelector";
import { Input } from "../../../../../components/FormElements/Input";
import { useAuth } from "../../../../../contexts/AuthContext";
import { useExpertInternationalDataMutation } from "../../../../../services/hooks/experts/mutations/useExpertInternationalDataMutation";
import { type ExpertDomain } from "../../../../../types/domain/expert-domain";
import { type InternationalPersonPersistence } from "../../../../../types/persistence/international-person-persistence";
import { getErrorMessage } from "../../../../../utils/getErrorMessage";
import { isEmailAvailable } from "../../../../../utils/isEmailAvailable";
import { internationalPersonSchema } from "../../../../../validators/internationalPersonSchema";
import register2 from "../../../../../assets/images/register-2.png";

interface Props {
  data: ExpertDomain | undefined;
  onGoToNextStep: () => void;
  onGoPreviousStep: () => void;
}

export const InternationalPerson = ({
  data,
  onGoToNextStep,
  onGoPreviousStep,
}: Props) => {
  const navigate = useNavigate();
  const { formatMessage } = useIntl();
  const { user } = useAuth();
  const { mutate, isLoading } = useExpertInternationalDataMutation();

  const { control, formState, handleSubmit, setError, clearErrors } =
    useForm<InternationalPersonPersistence>({
      mode: "onChange",
      resolver: yupResolver(internationalPersonSchema),
      defaultValues: {
        name: data?.name,
        email: data?.email,
        phone: data?.phone,
        linkedin: data?.linkedin || "",
        city: data?.naturalData?.city,
        state: data?.naturalData?.state,
        country: data?.naturalData?.country,
      },
    });

  const editNaturalPersonData = async (
    data: InternationalPersonPersistence
  ) => {
    if (user?.id) {
      mutate(
        { id: user?.id, data },
        {
          onSuccess() {
            onGoToNextStep();
          },
          onError(error: unknown) {
            toast(getErrorMessage(error), {
              position: "top-right",
              autoClose: 3000,
              hideProgressBar: true,
              closeOnClick: true,
              pauseOnHover: true,
              closeButton: false,
              icon: (
                <XCircle
                  size={48}
                  className="mx-auto my-auto text-alert-error"
                />
              ),
              className: "bg-red-50",
            });
          },
        }
      );
    }
  };

  return (
    <section className="md:py-5">
      <div className="container mx-auto">
        <div className="flex justify-center">
          <div className="hidden md:block w-1/4">
            <img src={register2} alt="Text with image" />
          </div>
          <div className="w-full md:w-3/4 md:ps-8">
            <form
              onSubmit={handleSubmit(editNaturalPersonData)}
              className="flex flex-col w-full h-full lg:max-w-[90%]"
            >
              <header className="flex flex-wrap mb-12">
                <h2 className="mb-6 text-2xl font-semibold">
                  {formatMessage({ id: "FINISH_YOUR_REGISTRATION_TITLE" })}
                </h2>
                <p className="">
                  {formatMessage({
                    id: "FINISH_YOUR_REGISTRATION_SUBTITLE",
                  })}
                </p>
              </header>

              <div className="w-full flex flex-col gap-[25px] mb-10">
                <div className="flex flex-wrap gap-5">
                  <div className="w-full md:w-[calc(50%-10px)]">
                    <Controller
                      name="name"
                      control={control}
                      render={({
                        field: { value = "", onChange },
                        fieldState: { error },
                      }) => (
                        <Input
                          label={formatMessage({ id: "FULL_NAME" })}
                          placeholder={formatMessage({ id: "FULL_NAME" })}
                          isValid={false}
                          value={value}
                          onChange={onChange}
                          errorMessage={
                            error?.message &&
                            formatMessage({ id: `${error?.message}` })
                          }
                        />
                      )}
                    />
                  </div>
                  <div className="w-full md:w-[calc(50%-10px)]">
                    <Controller
                      name="email"
                      control={control}
                      render={({
                        field: { value = "", onChange },
                        fieldState: { error },
                      }) => (
                        <Input
                          label="E-mail"
                          placeholder="exemple@email.com"
                          isValid={false}
                          value={value}
                          onChange={async (event) => {
                            onChange(event);

                            if (
                              !(await isEmailAvailable(
                                event.target.value,
                                user!.email
                              ))
                            ) {
                              setError("email", {
                                message: "EMAIL_ALREADY_IN_USE",
                              });
                              return;
                            }

                            clearErrors("email");
                          }}
                          errorMessage={
                            error?.message &&
                            formatMessage({ id: error?.message })
                          }
                        />
                      )}
                    />
                  </div>
                </div>
                <div className="flex gap-5">
                  <div className="w-[calc(40%-10px)]">
                    <Controller
                      name="phone"
                      control={control}
                      render={({
                        field: { value = "", onChange },
                        fieldState: { error },
                      }) => (
                        <Input
                          label={formatMessage({ id: "TELEPHONE" })}
                          placeholder={formatMessage({ id: "TELEPHONE" })}
                          isValid={false}
                          value={value}
                          onChange={onChange}
                          errorMessage={
                            error?.message &&
                            formatMessage({ id: `${error?.message}` })
                          }
                        />
                      )}
                    />
                  </div>
                  <div className="w-[calc(60%-10px)]">
                    <Controller
                      name="linkedin"
                      control={control}
                      render={({
                        field: { value = "", onChange },
                        fieldState: { error },
                      }) => (
                        <Input
                          label="LinkedIn"
                          placeholder="https://www.linkedin.com/in/example-user-name"
                          isValid={false}
                          value={value}
                          onChange={onChange}
                          errorMessage={
                            error?.message &&
                            formatMessage({ id: `${error?.message}` })
                          }
                        />
                      )}
                    />
                  </div>
                </div>
              </div>

              <div className="w-full">
                <p className="border-s-[12px] border-secondary-light ps-4 mb-10">
                  {formatMessage({
                    id: "WHY_IS_IMPORTANT_REGISTER_YOUR_LOCATION",
                  })}
                </p>
              </div>

              <div className="flex flex-col gap-[25px]">
                <div className="w-full md:w-[calc(40%-10px)]">
                  <Controller
                    name="country"
                    control={control}
                    render={({
                      field: { value = "", onChange },
                      fieldState: { error },
                    }) => (
                      <CountrySelector
                        value={value}
                        onValueChange={onChange}
                        placeholderMessageId={"COUNTRY"}
                        errorMessage={
                          error?.message &&
                          formatMessage({ id: `${error?.message}` })
                        }
                      />
                    )}
                  />
                </div>

                <div className="w-full flex gap-5 flex-row">
                  <div className="w-[calc(50%-10px)] md:w-[40%]">
                    <Controller
                      name="city"
                      control={control}
                      render={({
                        field: { value = "", onChange },
                        fieldState: { error },
                      }) => (
                        <Input
                          placeholder={formatMessage({ id: "CITY" })}
                          isValid={false}
                          value={value}
                          onChange={onChange}
                          errorMessage={
                            error?.message &&
                            formatMessage({ id: `${error?.message}` })
                          }
                        />
                      )}
                    />
                  </div>
                  <div className="w-[calc(50%-10px)] md:w-[40%]">
                    <Controller
                      name="state"
                      control={control}
                      render={({
                        field: { value = "", onChange },
                        fieldState: { error },
                      }) => (
                        <Input
                          placeholder={formatMessage({ id: "STATE" })}
                          isValid={false}
                          value={value}
                          onChange={onChange}
                          errorMessage={
                            error?.message &&
                            formatMessage({ id: `${error?.message}` })
                          }
                        />
                      )}
                    />
                  </div>
                </div>
              </div>

              <div className="w-full flex flex-col md:flex-row justify-center items-center gap-4 mt-14">
                <Button
                  onClick={onGoPreviousStep}
                  variant="outline"
                  borderColor="border-primary-light"
                  size="large"
                  className="ps-14 pe-14"
                >
                  {formatMessage({ id: "BACK" })}
                </Button>
                <Button
                  disabled={
                    !formState.isValid ||
                    Object.entries(formState.errors).length > 0
                  }
                  isLoading={isLoading}
                  type="submit"
                  size="large"
                  className="ps-12 pe-12"
                >
                  {formatMessage({ id: "CONTINUE" })}
                </Button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};
